import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { subscriptionCancellationApi } from '../api/subscription-cancellation-api';
import { useToast } from '@client/components/hooks/use-toast';

interface UseCancellationFeedbackOptions {
  onSuccess: () => void;
}

export function useCancellationFeedback({ onSuccess }: UseCancellationFeedbackOptions) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: () =>
      subscriptionCancellationApi.triggerCancellation({
        reason: selectedReason,
        additionalFeedback: additionalFeedback.trim() || undefined,
      }),
    onSuccess: data => {
      if (data.success) {
        const accessUntilDate = data.nextBillingDateTime
          ? new Date(data.nextBillingDateTime).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : 'the end of your billing period';

        toast({
          title: 'Your subscription has been cancelled',
          description: `You will continue to have access until ${accessUntilDate}. You will receive an email with confirmation and further details.`,
          variant: 'default',
          className: '-translate-y-5',
          duration: 5000,
        });
      }
      onSuccess();
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || error?.message;

      if (status === 403) {
        toast({
          title: 'Only the subscription owner can cancel the subscription.',
          description: 'Please contact the account owner to cancel this subscription.',
          variant: 'destructive',
          className: '-translate-y-5',
          duration: 5000,
        });
      } else if (status === 404) {
        toast({
          title: 'Subscription not found',
          description: 'Unable to locate your subscription. Please try again or contact support.',
          variant: 'destructive',
          className: '-translate-y-5',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Failed to save cancellation feedback',
          description: message || 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
          duration: 5000,
        });
      }
    },
  });

  const handleSubmit = () => {
    if (!selectedReason) return;
    if (selectedReason === 'other' && !additionalFeedback.trim()) return;

    mutation.mutate();
  };

  const isSubmitDisabled =
    !selectedReason || (selectedReason === 'other' && !additionalFeedback.trim());

  return {
    selectedReason,
    setSelectedReason,
    additionalFeedback,
    setAdditionalFeedback,
    handleSubmit,
    isSubmitDisabled,
    isPending: mutation.isPending,
  };
}
